import { mountCar3dPreview, type Car3dPreviewHandle } from './car3dPreview.js';

/**
 * Files d'attente de chargement partagée entre toutes les grilles (Bibliothèque,
 * sélecteur) : les fichiers `.glb` pèsent plusieurs Mo, et un défilement rapide peut
 * rendre des dizaines de cartes visibles d'un coup — sans limite, ça lance autant de
 * téléchargements simultanés et sature le serveur de dev (timeouts, 500, jusqu'à
 * couper la connexion Socket.io). On ne borne ici que le réseau ; le nombre de
 * contextes WebGL actifs reste géré séparément par l'observateur de visibilité.
 */
const MAX_CONCURRENT_LOADS = 4;
let activeLoads = 0;
const pending: Array<() => void> = [];

function scheduleLoad(start: () => void): () => void {
  let started = false;
  let cancelled = false;
  const task = (): void => {
    if (cancelled) return;
    started = true;
    activeLoads++;
    start();
  };
  if (activeLoads < MAX_CONCURRENT_LOADS) {
    task();
  } else {
    pending.push(task);
  }
  return () => {
    if (started) return;
    cancelled = true;
    const i = pending.indexOf(task);
    if (i !== -1) pending.splice(i, 1);
  };
}

function releaseLoadSlot(): void {
  activeLoads--;
  const next = pending.shift();
  next?.();
}

/**
 * Monte un aperçu 3D dans `container` seulement quand il devient visible à l'écran,
 * et le démonte quand il en sort. Utilisé pour les grilles de plusieurs modèles
 * (Bibliothèque, sélecteur) : chaque vignette est figée (`snapshot: true`) dès le
 * premier rendu pour libérer le contexte WebGL — les navigateurs plafonnent ce
 * nombre autour d'une quinzaine, d'où les canvas blancs « cassés » si trop de
 * renderers restent vivants. `scheduleLoad` borne en plus les téléchargements `.glb`.
 *
 * `root` doit être le conteneur qui défile réellement (`overflow-y: auto`) quand
 * la grille est dans une petite zone scrollable (modale de sélection) plutôt que
 * la page entière — sans lui, `IntersectionObserver` compare à la fenêtre du
 * navigateur, pas au découpage du conteneur, et considère TOUTES les cartes
 * comme visibles dès que la grille elle-même est à l'écran (même défilées hors
 * de sa propre zone), montant des dizaines de contextes WebGL d'un coup. Laisser
 * `root` à `null` (défaut) reste correct pour une grille en pleine page (rien à
 * défiler en interne, ex. la Bibliothèque d'assets).
 *
 * Marge de préchargement volontairement réduite (24px, pas les 80px habituels
 * d'un scroll de page) : dans une petite zone scrollable multi-colonnes, chaque
 * "ligne" de marge révèle autant de cartes que de colonnes — une marge large y
 * fait monter beaucoup plus de contextes GPU à la fois que sur un scroll pleine
 * page à une colonne.
 */
export function mountLazyCar3dPreview(
  container: HTMLElement,
  modelUrl: string,
  root?: Element | null
): () => void {
  let handle: Car3dPreviewHandle | null = null;
  let cancelQueued: (() => void) | null = null;
  let visible = false;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting;
        if (visible) {
          if (!handle && !cancelQueued) {
            cancelQueued = scheduleLoad(() => {
              cancelQueued = null;
              if (!visible) {
                releaseLoadSlot();
                return;
              }
              handle = mountCar3dPreview(container, modelUrl, releaseLoadSlot, {
                snapshot: true,
              });
            });
          }
        } else {
          cancelQueued?.();
          cancelQueued = null;
          handle?.dispose();
          handle = null;
        }
      }
    },
    { root: root ?? null, rootMargin: root ? '0px' : '80px' }
  );
  observer.observe(container);

  return () => {
    observer.disconnect();
    cancelQueued?.();
    cancelQueued = null;
    handle?.dispose();
    handle = null;
  };
}
